import ApplicationFilters from '@/components/DashboardFilters/ApplicationFilters';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import {
   Table,
   TableBody,
   TableHead,
   TableHeader,
   TableRow,
} from "@/components/ui/table";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useTabNavigation from '@/hooks/useTabNavigation';
import useTourGuide from '@/hooks/useTourGuide';
import { useTypewriter } from '@/hooks/useTypewriter';
import { getUserRoles } from '@/utils/auth';
import { FileX } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useApplications } from '../../../hooks/useApplications';
import { useOrderOfPayments } from '../../../hooks/useOrderOfPayments';
import ChiefApplicationRow from './ChiefApplicationRow';
import ChiefOOPRow from './ChiefOOPRow';

const ChiefDashboard = () => {
   const navigate = useNavigate();
   const location = useLocation();

   const [filters, setFilters] = useState({
      searchTerm: '',
      applicationType: '',
      dateRange: {
         from: undefined,
         to: undefined
      }
   });

   const mainTabs = useMemo(() => ['Applications Awaiting OOP', 'Order of Payment', 'Certificates'], []);
   const subTabs = useMemo(() => ({
      'Applications Awaiting OOP': ['Awaiting OOP', 'Created OOP'],
      'Order of Payment': ['Pending Signature', 'Signed Order Of Payment'],
      'Certificates': ['Pending Chief TSD Signature', 'Signed Certificates']
   }), []);

   // Initialize tab navigation first
   const {
      activeMainTab,
      activeSubTab,
      handleMainTabChange: handleTabChange,
      handleSubTabChange
   } = useTabNavigation({
      mainTabs,
      subTabs,
      defaultMainTab: 'Applications Awaiting OOP',
      defaultSubTab: 'Awaiting OOP'
   });

   let params = {};

   if (activeSubTab === 'Signed Certificates') {
      params.certificateSignedByChiefTSD = true;
      params.applicationType = 'Certificate of Verification';
   }

   // Use useMemo for query parameters
   const queryParams = useMemo(() => {
      switch (activeSubTab) {
         case 'Awaiting OOP':
            return { awaitingOOP: true };
         case 'Created OOP':
            return { OOPCreated: true, awaitingOOP: false };

         // case 'Reports for Review':
         //    return { currentStage: 'InspectionReportForReviewByChief', hasInspectionReport: true };
         // case 'Reviewed Reports':
         //    return { InspectionReportsReviewedByChief: true };

         case 'Pending Signature':
            return { OOPstatus: 'For Approval' };
         case 'Signed Order Of Payment':
            return { OOPSignedByTwoSignatories: true };
         case 'Pending Chief TSD Signature':
            return {
               hasCertificate: true,
               currentStage: 'PendingChiefTSDSignature',
               certificateSignedByForestRanger: true,
               certificateSignedByChiefTSD: false,
               applicationType: 'Certificate of Verification'
            };
         case 'Signed Certificates':
            return {
               // hasCertificate: true,
               currentStage: 'PendingSignatureByPENRCENROfficer',
               certificateSignedByChiefTSD: true,
               // currentStage: 'PendingSignatureByPENRCENROfficer',
               // applicationType: 'Certificate of Verification'
            };
         default:
            return { currentStage: 'ChiefRPSReview' };
      }
   }, [activeSubTab]);

   // Initialize queries with memoized parameters
   const { applications, loading: appLoading, error: appError, refetch: refetchApps } = useApplications(queryParams);
   const { applications: allApplications, refetch: refetchAllApplications } = useApplications({});
   const { oops, loading: oopsLoading, error: oopsError, refetch: refetchOOPs } = useOrderOfPayments();

   // Define handleRefetch
   const handleRefetch = useCallback(() => {
      if (activeMainTab === 'Order of Payment') {
         refetchOOPs();
      } else {
         refetchApps();
      }
      // Always refetch all applications for tab counts
      refetchAllApplications();
   }, [activeMainTab, refetchOOPs, refetchApps, refetchAllApplications]);

   // Update the tab navigation with the callback
   useEffect(() => {
      const params = new URLSearchParams(location.search);
      params.set('tab', activeMainTab);
      params.set('subtab', activeSubTab);
      navigate({
         pathname: location.pathname,
         search: params.toString()
      }, { replace: true });
   }, [activeMainTab, activeSubTab, navigate, location.pathname]);

   useEffect(() => {
      refetchApps();
      refetchAllApplications(); // Also refetch all applications when tab changes
   }, [refetchApps, refetchAllApplications, activeSubTab]);

   useEffect(() => {
      const pollInterval = setInterval(handleRefetch, 30000); // Poll every 30 seconds (increased from 5 seconds)
      return () => clearInterval(pollInterval);
   }, [handleRefetch]);

   const handleReviewComplete = () => {
      handleRefetch();
   };

   const filteredApplications = useMemo(() => {
      return applications.filter(app => {
         const matchesSearch = app.applicationNumber.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            app.applicationType.toLowerCase().includes(filters.searchTerm.toLowerCase());

         const matchesType = !filters.applicationType ||
            filters.applicationType === "all" ||
            app.applicationType === filters.applicationType;

         const matchesDateRange = (() => {
            if (!filters.dateRange.from && !filters.dateRange.to) return true;
            const appDate = new Date(app.dateOfSubmission);
            appDate.setHours(0, 0, 0, 0);
            const fromDate = filters.dateRange.from ? new Date(filters.dateRange.from) : null;
            const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : null;
            if (fromDate) fromDate.setHours(0, 0, 0, 0);
            if (toDate) toDate.setHours(0, 0, 0, 0);
            return (!fromDate || appDate >= fromDate) && (!toDate || appDate <= toDate);
         })();

         return matchesSearch && matchesType && matchesDateRange;
      });
   }, [applications, filters]);

   const filteredOOPs = useMemo(() => {
      return oops.filter(oop => {
         const matchesStatus = (() => {
            if (activeSubTab === 'Pending Signature') {
               return oop.OOPstatus === 'Pending Signature';
            } else if (activeSubTab === 'Signed Order Of Payment') {
               return oop.OOPSignedByTwoSignatories === true;
            }
            return true;
         })();

         const matchesSearch = oop.billNo?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            oop.applicationId?.toLowerCase().includes(filters.searchTerm.toLowerCase());

         const matchesType = !filters.applicationType ||
            filters.applicationType === "all" ||
            oop.natureOfApplication === filters.applicationType;

         const matchesAmount = !filters.amountRange || (() => {
            const amount = parseFloat(oop.totalAmount);
            switch (filters.amountRange) {
               case '0-1000': return amount >= 0 && amount <= 1000;
               case '1001-5000': return amount > 1000 && amount <= 5000;
               case '5001-10000': return amount > 5000 && amount <= 10000;
               case '10001+': return amount > 10000;
               default: return true;
            }
         })();

         const matchesDateRange = (() => {
            if (!filters.dateRange.from && !filters.dateRange.to) return true;
            const oopDate = new Date(parseInt(oop.createdAt));
            if (isNaN(oopDate.getTime())) return true;
            oopDate.setHours(0, 0, 0, 0);
            const fromDate = filters.dateRange.from ? new Date(filters.dateRange.from) : null;
            const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : null;
            if (fromDate) fromDate.setHours(0, 0, 0, 0);
            if (toDate) toDate.setHours(0, 0, 0, 0);
            return (!fromDate || oopDate >= fromDate) && (!toDate || oopDate <= toDate);
         })();

         return matchesStatus && matchesSearch && matchesType && matchesAmount && matchesDateRange;
      });
   }, [oops, activeSubTab, filters]);

   const getStatusColor = (status) => {
      switch (status.toLowerCase()) {
         case 'pending signature': return 'bg-orange-100 text-orange-800';
         case 'For Approval': return 'bg-purple-100 text-purple-800';
         default: return 'bg-gray-100 text-gray-800';
      }
   };

   const isMobile = useMediaQuery('(max-width: 640px)');

   const isChrome = useMemo(() => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isBrave = navigator.brave !== undefined;
      return userAgent.includes('chrome') && !userAgent.includes('edg') && !isBrave;
   }, []);

   const descriptions = useMemo(() => ({
      'Awaiting OOP': 'Applications awaiting Order of Payment creation',
      'Created OOP': 'Applications with created Order of Payments',
      'Pending Signature': 'Order of Payments pending signature',
      'Signed Order Of Payment': 'Signed Order of Payments',
      'Pending Chief TSD Signature': 'Certificates awaiting signature',
      'Signed Certificates': 'Certificates with completed signatures'
   }), []);

   const descriptionText = useTypewriter(descriptions[activeSubTab] || '');

   const renderTabDescription = () => (
      <div className="mb-4 -mt-4">
         <h1 className="text-sm min-h-[20px] text-black dark:text-gray-300">{descriptionText}</h1>
      </div>
   );

   const [currentPage, setCurrentPage] = useState(1);
   const [itemsPerPage, setItemsPerPage] = useState(10);
   const [totalItems, setTotalItems] = useState(0);

   useEffect(() => {
      if (activeMainTab === 'Applications Awaiting OOP') {
         setTotalItems(filteredApplications.length);
      } else {
         setTotalItems(filteredOOPs.length);
      }
   }, [filteredApplications, filteredOOPs, activeMainTab]);

   const renderOrderOfPaymentTable = () => {
      if (oopsLoading) return <div className="flex justify-center py-8">Loading order of payments...</div>;
      if (oopsError) return <div className="text-destructive text-center py-8">Error loading order of payments</div>;

      if (!filteredOOPs || filteredOOPs.length === 0) {
         return (
            <div className="text-center py-8">
               <FileX className="mx-auto h-12 w-12 text-muted-foreground" />
               <h3 className="mt-2 text-lg font-semibold">No orders of payment found</h3>
               <p className="text-sm text-muted-foreground">
                  {filters.applicationType ?
                     `No orders of payment found for ${filters.applicationType}` :
                     'No orders of payment available'}
               </p>
            </div>
         );
      }

      return (
         <Table>
            <TableHeader>
               <TableRow>
                  <TableHead className="w-[25%]">Application Number</TableHead>
                  <TableHead className="w-[15%] text-center">Bill Number</TableHead>
                  <TableHead className="w-[15%] text-center">Date</TableHead>
                  <TableHead className="w-[15%] text-center">Amount</TableHead>
                  <TableHead className="w-[15%] text-center">Status</TableHead>
                  <TableHead className="w-[15%] text-center">Actions</TableHead>
               </TableRow>
            </TableHeader>
            <TableBody>
               {filteredOOPs
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((oop) => (
                     <ChiefOOPRow
                        key={oop._id}
                        oop={oop}
                        onRefetch={handleRefetch}
                        currentTab={activeSubTab}
                     />
                  ))}
            </TableBody>
         </Table>
      );
   };

   const renderApplicationTable = () => {
      if (appLoading) return <div className="flex justify-center py-8">Loading applications...</div>;
      if (appError) return <div className="text-destructive text-center py-8">Error loading applications</div>;
      if (filteredApplications.length === 0) {
         return (
            <div className="text-center py-8">
               <FileX className="mx-auto h-12 w-12 text-muted-foreground" />
               <h3 className="mt-2 text-lg font-semibold">No applications found</h3>
               <p className="text-sm text-muted-foreground">
                  {filters.applicationType ?
                     `No applications found for ${filters.applicationType}` :
                     'No applications available'}
               </p>
            </div>
         );
      }

      if (isMobile) {
         return (
            <div className="space-y-4">
               {filteredApplications.map((app) => (
                  <ChiefApplicationRow
                     key={app.id}
                     app={app}
                     onReviewComplete={handleReviewComplete}
                     getStatusColor={getStatusColor}
                     currentTab={activeSubTab}
                     isMobile={true}
                  />
               ))}
            </div>
         );
      }

      return (
         <Table>
            <TableHeader>
               <TableRow>
                  <TableHead className="w-[20%]">Application Number</TableHead>
                  <TableHead className="w-[20%] text-center">Application Type</TableHead>
                  <TableHead className="w-[15%] text-center">Request Type</TableHead>
                  <TableHead className="w-[15%] text-center">Date</TableHead>
                  <TableHead className="w-[15%] text-center">Status</TableHead>
                  <TableHead className="w-[15%] text-center">Actions</TableHead>
               </TableRow>
            </TableHeader>
            <TableBody>
               {filteredApplications
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((app) => (
                     <ChiefApplicationRow
                        key={app.id}
                        app={app}
                        onReviewComplete={handleReviewComplete}
                        getStatusColor={getStatusColor}
                        currentTab={activeSubTab}
                        isMobile={false}
                     />
                  ))}
            </TableBody>
         </Table>
      );
   };

   const userRoles = getUserRoles();

   const getTabCounts = useMemo(() => {
      if (!allApplications || !oops) return {};

      const counts = {};

      // Calculate counts for all tabs regardless of active tab
      // Applications Awaiting OOP
      counts['Awaiting OOP'] = allApplications.filter(app =>
         app.awaitingOOP &&
         !app.OOPCreated
      ).length;

      counts['Created OOP'] = allApplications.filter(app =>
         app.OOPCreated === true
      ).length;

      // Order of Payment
      counts['Pending Signature'] = oops?.filter(oop =>
         oop.OOPstatus === 'Pending Signature'
      ).length || 0;

      counts['Signed Order Of Payment'] = oops?.filter(oop =>
         oop.OOPSignedByTwoSignatories === true
      ).length || 0;

      // console.log('Tab Counts:', counts);
      return counts;
   }, [allApplications, oops]);

   // Keyboard navigation
   useEffect(() => {
      const handleKeyPress = (e) => {
         if (e.altKey) {
            switch (e.key) {
               case 'ArrowRight':
               case 'ArrowLeft':
               case 'ArrowUp':
               case 'ArrowDown':
                  // Prevent default browser navigation
                  e.preventDefault();

                  if (e.key === 'ArrowRight') {
                     const currentMainIndex = mainTabs.indexOf(activeMainTab);
                     if (currentMainIndex < mainTabs.length - 1) {
                        handleTabChange(mainTabs[currentMainIndex + 1]);
                     }
                  } else if (e.key === 'ArrowLeft') {
                     const prevMainIndex = mainTabs.indexOf(activeMainTab);
                     if (prevMainIndex > 0) {
                        handleTabChange(mainTabs[prevMainIndex - 1]);
                     }
                  } else if (e.key === 'ArrowDown') {
                     const currentSubTabs = subTabs[activeMainTab];
                     const currentSubIndex = currentSubTabs.indexOf(activeSubTab);
                     if (currentSubIndex < currentSubTabs.length - 1) {
                        handleSubTabChange(currentSubTabs[currentSubIndex + 1]);
                     }
                  } else if (e.key === 'ArrowUp') {
                     const currentSubTabsList = subTabs[activeMainTab];
                     const prevSubIndex = currentSubTabsList.indexOf(activeSubTab);
                     if (prevSubIndex > 0) {
                        handleSubTabChange(currentSubTabsList[prevSubIndex - 1]);
                     }
                  }
                  break;
            }
         }
      };

      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
   }, [activeMainTab, activeSubTab, mainTabs, subTabs, handleTabChange, handleSubTabChange]);

   const { startTour: initiateTour } = useTourGuide({
      tourId: 'chief-tour',
      onFinish: () => {
         console.log('Tour finished');
      },
   });

   const chiefTourSteps = useMemo(() => {
      // Base steps that are always available
      const baseSteps = [
         {
            target: '.main-tabs',
            content: 'Switch between Applications Awaiting OOP and Order of Payment using these main tabs',
            placement: 'bottom',
            disableBeacon: true,
         },
         {
            target: '.sub-tabs',
            content: 'View different stages of applications and order of payments',
            placement: 'bottom',
         },
         {
            target: '.applications-filters',
            content: 'Filter items by type, status, and date range',
            placement: 'bottom',
         }
      ];

      // Add table steps based on whether we have data or not
      if (activeMainTab === 'Applications Awaiting OOP' && filteredApplications?.length > 0) {
         baseSteps.push(
            {
               target: '.applications-table',
               content: 'View all applications that need your attention',
               placement: 'top',
            },
            {
               target: '.table-actions',
               content: 'Here you can find different actions for each application',
               placement: 'left',
            },
            {
               target: '.view-action',
               content: 'Click here to view the complete details of the application',
               placement: 'left',
            },
            {
               target: '.undo-action',
               content: 'Click here to undo previous actions if needed',
               placement: 'left',
            }
         );
      } else if (activeMainTab === 'Order of Payment' && filteredOOPs?.length > 0) {
         baseSteps.push(
            {
               target: '.applications-table',
               content: 'View all Order of Payments that need your attention',
               placement: 'top',
            },
            {
               target: '.table-actions',
               content: 'Here you can find different actions for each OOP',
               placement: 'left',
            },
            {
               target: '.view-oop-action',
               content: 'Click here to view the complete details of the Order of Payment',
               placement: 'left',
            },
            {
               target: '.print-oop-action',
               content: 'Click here to print the Order of Payment',
               placement: 'left',
            },
            {
               target: '.sign-oop-action',
               content: 'Click here to affix your e-signature on the Order of Payment',
               placement: 'left',
            },
            {
               target: '.undo-oop-action',
               content: 'Click here to undo OOP creation or signature if needed',
               placement: 'left',
            }
         );
      } else {
         baseSteps.push({
            target: '.applications-table',
            content: 'Items will appear here once they need your attention',
            placement: 'top',
         });
      }

      return baseSteps;
   }, [activeMainTab, filteredApplications?.length, filteredOOPs?.length]);

   // Add useEffect for tour initialization
   useEffect(() => {
      const tourButton = document.querySelector('.help-tour-button');
      if (tourButton) {
         tourButton.disabled = appLoading || oopsLoading;
      }
   }, [appLoading, oopsLoading]);

   const handleStartTour = useCallback(async () => {
      // Wait for data to be loaded
      if (appLoading || oopsLoading) {
         toast.info('Please wait while data is loading...');
         return;
      }

      // Start the tour
      initiateTour(chiefTourSteps);
   }, [appLoading, oopsLoading, initiateTour, chiefTourSteps]);

   return (
      <DashboardLayout
         title={`${userRoles.includes('Chief_RPS') ? 'Chief RPS' : 'Chief TSD'} Dashboard`}
         description="Manage and process applications and order of payments"
         onRefresh={handleRefetch}
         isMobile={isMobile}
         mainTabs={mainTabs}
         subTabs={subTabs}
         activeMainTab={activeMainTab}
         activeSubTab={activeSubTab}
         onMainTabChange={handleTabChange}
         onSubTabChange={handleSubTabChange}
         tabDescription={renderTabDescription()}
         filters={<ApplicationFilters filters={filters} setFilters={setFilters} />}
         tabCounts={getTabCounts}
         helpTour={handleStartTour}
         currentPage={currentPage}
         totalItems={totalItems}
         itemsPerPage={itemsPerPage}
         onPageChange={setCurrentPage}
         onItemsPerPageChange={setItemsPerPage}
      >
         {activeMainTab === 'Order of Payment' ?
            renderOrderOfPaymentTable() :
            renderApplicationTable()
         }
      </DashboardLayout>
   );
};

export default ChiefDashboard;
